import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RoleCombination, RoleCombinationsProps } from "./RoleCombinations.types";
import CombinationRow from "./CombinationRow";
import { fetchExtendedWgData, updateStaffRoleCombinations } from "@/api";
import type { BusinessAccountExtendedWgItemData } from "@/types/needsContrains";

export default function RoleCombinations({ roles, onChange }: Readonly<RoleCombinationsProps>) {
    const [combinations, setCombinations] = useState<RoleCombination[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [updateToken, setUpdateToken] = useState<number>(1);
    const lastFetchedToken = useRef<number | null>(null);

    useEffect(() => {
        if (lastFetchedToken.current === updateToken) {
            return;
        }
        lastFetchedToken.current = updateToken;

        fetchExtendedWgData({ returnRelatedStaffRoles: true }).then((data: BusinessAccountExtendedWgItemData) => {
            const dicOfStaffRoles = data.dicOfStaffRoles ?? {};

            const loaded: RoleCombination[] = roles.map(role => {
                const staffRole = dicOfStaffRoles[role.id];
                const whiteList = staffRole?.simulShiftsConfig?.dicOfWhiteListRules ?? {};
                return {
                    id: crypto.randomUUID(),
                    roleId: role.id,
                    allowedRoleIds: Object.keys(whiteList),
                };
            });
            
            setCombinations(loaded);
        });
    }, [updateToken, roles]);

    useEffect(() => {
        setCombinations(prev => {
            const prevMap = new Map(prev.map(c => [c.roleId, c]));
            return roles.map(role =>
                prevMap.get(role.id) ?? { id: crypto.randomUUID(), roleId: role.id, allowedRoleIds: [] }
            );
        });
    }, [roles]);

    const implicitMap = useMemo(() => {
        const map = new Map<string, string[]>();

        for (const combo of combinations) {
            const existingImplicits: string[] = [];

            for (const other of combinations) {
                if (other.id === combo.id) {
                    continue;
                }

                if (other.allowedRoleIds.includes(combo.roleId)) {
                    if (!combo.allowedRoleIds.includes(other.roleId)) {
                        existingImplicits.push(other.roleId);
                    }
                }
            }

            map.set(combo.id, existingImplicits);
        }

        return map;
    }, [combinations]);

    const handleSave = useCallback(async (id: string, allowedRoleIds: string[]) => {
        const next = combinations.map(combination => combination.id === id ? {
            ...combination,
            allowedRoleIds
        } : combination);

        setCombinations(next);
        setEditingId(null);
        onChange?.(next);

        setIsSaving(true);
        try {
            const edited = next.find(c => c.id === id)!;
            await updateStaffRoleCombinations(edited.roleId, edited.allowedRoleIds);

            setUpdateToken(prev => prev + 1);
        } catch (err) {
            console.error("Failed to save staff role combinations", err);
        } finally {
            setIsSaving(false);
        }
    }, [combinations, onChange]);

    return (
        <div className="w-full py-4">
            <p className="text-sm text-gray-600 mb-4">
                Toutes les combinaisons de rôles sont interdites, à l'exception de ces combinaisons :
            </p>

            <div className="overflow-hidden rounded-lg border border-gray-300">
                <table className="w-full border-collapse text-center text-sm">
                    <thead className="text-white">
                    <tr>
                        <th className="border border-gray-300 bg-blue-600 px-2 py-1 font-semibold text-left w-[200px]">Rôle</th>
                        <th className="border border-gray-300 bg-blue-600 px-2 py-1 font-semibold text-left">
                            peut être fait avec
                        </th>
                        <th className="border border-gray-300 bg-blue-600 px-2 py-1 font-semibold w-[100px]">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {combinations.map(combo => (
                        <CombinationRow
                            key={combo.id}
                            combination={combo}
                            roles={roles}
                            implicitRoleIds={implicitMap.get(combo.id) ?? []}
                            isEditing={editingId === combo.id}
                            onEdit={() => setEditingId(combo.id)}
                            onSave={(allowedRoleIds) => handleSave(combo.id, allowedRoleIds)}
                            onCancel={() => setEditingId(null)}
                        />
                    ))}
                    </tbody>
                </table>
            </div>

            {isSaving && (
                <p className="text-sm text-gray-500 mt-2">Sauvegarde en cours...</p>
            )}
        </div>
    );
}
